import { isAxiosError } from 'axios'

import { useApiClient } from '@/composables/useApiClient'
import { useToast } from 'primevue'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@/stores/useAuthStore'
import type { IFilterContact } from './interfaces/filter-contact.interface'
import type { IPaginationResponse } from '../interfaces/pagination-response.interface'
import type { ContactDto } from './interfaces/contact.interface'
import type { ContactListItemDto } from './interfaces/contact-list-item.interface'
import type { IUpdateContact } from './interfaces/update-contact.interface'
import type { ICreateContact } from './interfaces/create-contact.interface'
import { ContactStatus } from './enums/contact-status.enum'

export interface ISearchContact {
  contact_id?: string
  phone?: string
  campaign_id?: string
}

type ContactsCountResponse = number | { count?: number; total?: number; data?: number }
type CachedContact = { contact: ContactDto; expiresAt: number }

const CONTACT_SEARCH_CACHE_TTL_MS = 10_000
const CONTACT_ALREADY_EXISTS_CODE = 1710
const contactSearchRequests = new Map<string, Promise<ContactDto>>()
const contactSearchCache = new Map<string, CachedContact>()

const getContactSearchKey = (params: ISearchContact, userId?: string | null) =>
  JSON.stringify([
    userId ?? 'anonymous',
    params.contact_id ?? null,
    params.phone ?? null,
    params.campaign_id ?? null,
  ])

const cacheContact = (contact: ContactDto, userId?: string | null) => {
  contactSearchCache.set(
    getContactSearchKey({ phone: contact.phone, campaign_id: contact.campaign_id }, userId),
    { contact, expiresAt: Date.now() + CONTACT_SEARCH_CACHE_TTL_MS },
  )
}

const replaceCachedContact = (contact: ContactDto, userId?: string | null) => {
  contactSearchCache.forEach((cached, key) => {
    if (cached.contact.id === contact.id) {
      contactSearchCache.set(key, {
        contact,
        expiresAt: Date.now() + CONTACT_SEARCH_CACHE_TTL_MS,
      })
    }
  })
  cacheContact(contact, userId)
}

const CONTACT_CREATION_ERROR_MESSAGE_KEYS: Record<number, string> = {
  [CONTACT_ALREADY_EXISTS_CODE]: 'contact.error_contact_already_exists',
}

const getApiErrorCode = (error: unknown): number | undefined => {
  if (!isAxiosError(error)) return undefined

  const code = (error.response?.data as { code?: number | string } | undefined)?.code
  if (code === undefined || code === null) return undefined

  const parsedCode = Number(code)
  return Number.isNaN(parsedCode) ? undefined : parsedCode
}

export const getContactCreationErrorMessageKey = (error: unknown): string => {
  const errorCode = getApiErrorCode(error)
  const mappedMessageKey =
    errorCode !== undefined ? CONTACT_CREATION_ERROR_MESSAGE_KEYS[errorCode] : undefined

  return mappedMessageKey ?? 'contact.error_creating_contact'
}

const buildContactsQueryParams = (query?: IFilterContact) => {
  const params = new URLSearchParams()

  if (!query) return params

  if (query.search) params.append('search', query.search)
  if (query.campaign_id) params.append('campaign_id', query.campaign_id)
  if (query.sort) params.append('sort', query.sort)
  if (query.direction) params.append('direction', query.direction)
  if (query.page) params.append('page', String(query.page))
  if (query.size) params.append('size', String(query.size))

  query.status?.forEach((status) => {
    params.append('status', status)
  })

  return params
}

export const useContactService = () => {
  const privateApi = useApiClient(true)
  const authStore = useAuthStore()
  const toast = useToast()
  const { t } = useI18n()

  const showToast = (type: 'success' | 'error', messageKey: string) => {
    toast.add({
      severity: type,
      summary: t(type === 'success' ? 'general.success' : 'general.error'),
      detail: t(messageKey),
      life: 3000,
    })
  }

  const handleError = (
    error: unknown,
    messageKey: string,
    options?: { showToast?: boolean; messageKeysByCode?: Record<number, string> },
  ) => {
    if (options?.showToast === false) return

    const errorCode = getApiErrorCode(error)
    const mappedMessageKey =
      errorCode !== undefined ? options?.messageKeysByCode?.[errorCode] : undefined

    showToast('error', mappedMessageKey ?? messageKey)
  }

  const getContacts = async (
    query?: IFilterContact,
  ): Promise<IPaginationResponse<ContactListItemDto>> => {
    try {
      return await privateApi.get<IPaginationResponse<ContactListItemDto>>('/contacts', {
        params: buildContactsQueryParams(query),
      })
    } catch (error) {
      handleError(error, 'contact.error_fetching_all_contacts')
      throw error
    }
  }

  const getContactById = async (contactId: string): Promise<ContactDto> => {
    try {
      const contact = await privateApi.get<ContactDto>(`/contacts/${contactId}`)
      replaceCachedContact(contact, authStore.userId)
      return contact
    } catch (error) {
      handleError(error, 'contact.error_fetching_contact')
      throw error
    }
  }

  const createContact = async (
    contactData: ICreateContact,
    options?: { showSuccessToast?: boolean; showErrorToast?: boolean },
  ): Promise<ContactDto> => {
    try {
      const result = await privateApi.post<ContactDto, ICreateContact>('/contacts', {
        ...contactData,
        status: contactData.status ?? ContactStatus.PENDING,
      })

      if (options?.showSuccessToast !== false) {
        showToast('success', 'contact.success_created_contact')
      }

      cacheContact(result, authStore.userId)
      return result
    } catch (error) {
      handleError(error, 'contact.error_creating_contact', {
        showToast: options?.showErrorToast,
        messageKeysByCode: CONTACT_CREATION_ERROR_MESSAGE_KEYS,
      })
      throw error
    }
  }

  const createContacts = async (contactsData: ICreateContact[]): Promise<ContactDto[]> => {
    const createdContacts = await Promise.all(
      contactsData.map((contactData) => createContact(contactData, { showSuccessToast: false })),
    )

    showToast('success', 'contact.success_created_contact')
    return createdContacts
  }

  const updateContact = async (
    contactId: string,
    updateData: IUpdateContact,
  ): Promise<ContactDto> => {
    try {
      const result = await privateApi.patch<ContactDto>(`/contacts/${contactId}`, updateData)
      replaceCachedContact(result, authStore.userId)
      showToast('success', 'contact.success_updated_contact')
      return result
    } catch (error) {
      handleError(error, 'contact.error_updating_contact')
      throw error
    }
  }

  const searchContact = (
    params: ISearchContact,
    options?: { showErrorToast?: boolean },
  ): Promise<ContactDto> => {
    const requestKey = getContactSearchKey(params, authStore.userId)
    const cached = contactSearchCache.get(requestKey)
    if (cached && cached.expiresAt > Date.now()) return Promise.resolve(cached.contact)
    if (cached) contactSearchCache.delete(requestKey)

    const pendingRequest = contactSearchRequests.get(requestKey)
    if (pendingRequest) return pendingRequest

    const request = privateApi
      .get<ContactDto>(`/contacts/search`, {
        params,
      })
      .then((contact) => {
        contactSearchCache.set(requestKey, {
          contact,
          expiresAt: Date.now() + CONTACT_SEARCH_CACHE_TTL_MS,
        })
        cacheContact(contact, authStore.userId)
        return contact
      })
      .catch((error: unknown) => {
        handleError(error, 'contact.error_fetching_contact', {
          showToast: options?.showErrorToast,
        })
        throw error
      })
      .finally(() => {
        contactSearchRequests.delete(requestKey)
      })

    contactSearchRequests.set(requestKey, request)
    return request
  }

  const getContactsCount = async (
    campaignId: string,
    options?: { showErrorToast?: boolean },
  ): Promise<number> => {
    try {
      const response = await privateApi.get<ContactsCountResponse>('/contacts/count', {
        params: { campaign_id: campaignId },
      })

      if (typeof response === 'number') return response

      return Number(response.count ?? response.total ?? response.data ?? 0)
    } catch (error) {
      handleError(error, 'contact.error_fetching_all_contacts', {
        showToast: options?.showErrorToast,
      })
      throw error
    }
  }

  return {
    createContact,
    createContacts,
    getContacts,
    getContactById,
    updateContact,
    searchContact,
    getContactsCount,
  }
}
